import { Globe, tile, utils, zeroTexture } from "@hscmap/stellar-globe";
import axios from 'axios'
import { TileTextureProviderComponentBase } from '@/stellar-globe-components';
import { MixerType, Params } from "@/modules/SspDataModule";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { ImageFilter } from '@/utils/image_filter';
import { store } from '@/store';

@Component
export default class TextureProviderComponent extends TileTextureProviderComponentBase<TextureProvider> {
  @Prop({ required: true })
  baseUrl!: string

  @Prop({ required: true })
  params!: Params

  created() {
    this.$watch(() => [this.params.filter, this.params.type], () => {
      this.updateParams(true)
      this.tp.setImageFilter()
    })
    this.$watch(() => this.params, () => {
      this.updateParams()
    }, { deep: true })
    this.$watch(() => store.config.tileLayer.enableMagFilter, () => {
      this.updateParams(true)
    })
  }

  newTextureProvider() {
    return new TextureProvider(this.globe, this.baseUrl, this)
  }
}

class SimpleRgbMixerPng extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly params: Params["simpleRgb"]) {
    super(gl)
  }

  fragmentShader() {
    return require('./glsl/simple_rgb_png_mixer.frag.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_a: this.params.a,
      u_bias: this.params.bias,
      u_beta: this.params.beta,
      u_b0: this.params.b0,
    })
  }
}

class SdssTrueColorMixerPng extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly params: Params["sdssTrueColor"]) {
    super(gl)
  }

  fragmentShader() {
    return require('./glsl/sdss_true_color_png_mixer.frag.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_a: this.params.a,
      u_bias: this.params.bias,
      u_beta: this.params.beta,
      u_b0: this.params.b0,
    })
  }
}

export class TextureProvider extends tile.AsyncTextureProvider {
  private tracts: tile.Tract[] = []
  private tractName = new Map<tile.Tract, string>()
  private imageFilter!: ImageFilter

  constructor(globe: Globe, public baseUrl: string, readonly ttc: TextureProviderComponent) {
    super(globe)
    this.setImageFilter()
    this.setBaseUrl(baseUrl)
  }

  release() {
    this.imageFilter.release()
    super.release()
  }

  private setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl
    this.loadMetadata()
  }

  setImageFilter() {
    this.imageFilter && this.imageFilter.release()
    switch (this.ttc.params.type) {
      case MixerType.SDSS_TRUE_COLOR:
        this.imageFilter = new SdssTrueColorMixerPng(this.globe.gl, this.ttc.params.sdssTrueColor)
        break
      case MixerType.SIMPLE_RGB:
        this.imageFilter = new SimpleRgbMixerPng(this.globe.gl, this.ttc.params.simpleRgb)
        break
    }
  }

  private async loadMetadata() {
    const { baseUrl } = this
    const { data: tracts } = await axios.get(`${this.baseUrl}/meta.json`)
    if (baseUrl !== this.baseUrl) {
      return
    }
    this.tracts = []
    this.tractName.clear()
    for (const name of Object.keys(tracts)) {
      const tract = tile.Tract.fromFitsHeader((tracts as any)[name].wcs)
      this.tractName.set(tract, name)
      this.tracts.push(tract)
    }
  }

  walkTracts(cb: (tract: tile.Tract) => void) {
    for (const t of this.tracts) {
      cb(t)
    }
  }

  async makeTileTexture(ref: tile.TileRef) {
    const tt = new tile.TileTexture(this)
    const urls = this.ttc.params.filter.map(
      (f) => `${this.baseUrl}/${f}/${this.tractName.get(ref.tract)}/${ref.level}/${ref.p}/${ref.q}.png`)
    const images = await Promise.all(urls.map((url) => utils.loadImage(url, zeroTexture)))
    const gl = this.globe.gl
    const { tileSize } = ref.tract

    if (this.alreadyReleased) {
      return tt
    }

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
    this.imageFilter.applyToImages(tt.tex, tileSize, tileSize, images)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
    tt.tex.bind(() => {
      if (ref.level === ref.tract.maxTileLevel) {
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
      }
      if (!this.ttc.Store.config.tileLayer.enableMagFilter && ref.level === 0) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      }
    })
    return tt
  }
}
